//import { UserProfileInterface } from "@/store/Profile/ProfileInterfaces";
import {
  ref,
  getDownloadURL,
  uploadString,
  deleteObject,
} from "firebase/storage";
import { storage } from "../firebase";

const STATIC_PATH_PREFIX = "img/gtr-images/";
const STATIC_TTL_MS = 24 * 60 * 60 * 1000;
const DYNAMIC_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  promise: Promise<string>;
  url?: string;
  expiresAt: number;
}

const PROFILE_PICTURE_DEFAULT =
  "img/gtr-images/avatars/character/avatar-x-reloj-arena.jpg";

const urlCache = new Map<string, CacheEntry>();

function getTtl(path: string): number {
  return path.startsWith(STATIC_PATH_PREFIX) ? STATIC_TTL_MS : DYNAMIC_TTL_MS;
}

function cacheKey(path: string, defaultImage?: string): string {
  return defaultImage ? `${path}|${defaultImage}` : path;
}

function invalidateCacheFor(path: string) {
  for (const key of urlCache.keys()) {
    if (key === path || key.startsWith(`${path}|`)) {
      urlCache.delete(key);
    }
  }
}

async function doGetImage(path: string, defaultImage?: string) {
  const fallbackImg = "img/gtr-images/gtr-no-image.png";
  const defaultImg = defaultImage || fallbackImg;

  const key = cacheKey(path, defaultImage);
  const cached = urlCache.get(key);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.promise;
  }
  if (cached) urlCache.delete(key);

  const pathRef = ref(storage, path);
  const promise = getDownloadURL(pathRef)
    .then((url) => url)
    .catch(() => {
      return getDownloadURL(ref(storage, defaultImg))
        .then((url) => url)
        .catch(() => {
          return getDownloadURL(ref(storage, fallbackImg)).then((url) => url);
        });
    });

  const entry: CacheEntry = {
    promise,
    expiresAt: Date.now() + getTtl(path),
  };
  urlCache.set(key, entry);

  promise
    .then((url) => {
      entry.url = url;
    })
    .catch(() => {
      // If resolution fails outright, drop the entry so a later call can retry.
      const current = urlCache.get(key);
      if (current && current.promise === promise) {
        urlCache.delete(key);
      }
    });

  return promise;
}

function getCachedImageUrlInternal(
  path: string,
  defaultImage?: string
): string | null {
  const key = cacheKey(path, defaultImage);
  const entry = urlCache.get(key);
  if (!entry) return null;
  if (Date.now() >= entry.expiresAt) {
    urlCache.delete(key);
    return null;
  }
  return entry.url ?? null;
}

async function doUpdateImage(
  path: string,
  dataBase64Url: string
): Promise<boolean> {
  const pathRef = ref(storage, path);

  const response = uploadString(pathRef, dataBase64Url, "data_url")
    .then(() => {
      invalidateCacheFor(path);
      return true;
    })
    .catch((error) => {
      console.error(error);
      return Promise.reject(error);
    });
  return await response;
}

async function doDeleteImage(pictureURL: string) {
  const toDeleteRef = ref(storage, pictureURL);

  if (!toDeleteRef.fullPath.startsWith("img/gtr-images/")) {
    const response = deleteObject(toDeleteRef)
      .then(() => {
        invalidateCacheFor(pictureURL);
        return true;
      })
      .catch((error) => {
        console.error(error);
        return false;
      });
    return await response;
  } else {
    return true;
  }
}

export function getCachedImageUrl(
  path: string,
  defaultImage?: string
): string | null {
  return getCachedImageUrlInternal(path, defaultImage);
}

export function getCachedProfilePictureUrl(path: string): string | null {
  return getCachedImageUrlInternal(path, PROFILE_PICTURE_DEFAULT);
}

export const images = {
  methods: {
    getImage(path: string, defaultImage?: string): Promise<string> {
      return doGetImage(path, defaultImage);
    },
    getProfilePicture(path: string): Promise<string> {
      return doGetImage(path, PROFILE_PICTURE_DEFAULT);
    },
    setImage(request: any): Promise<boolean> {
      return doUpdateImage(request.pictureUrl, request.dataBase64Url);
    },
    removeImage(pictureURL: string): Promise<boolean> {
      return doDeleteImage(pictureURL);
    },
    invalidateImage(path: string): void {
      invalidateCacheFor(path);
    },
  },
};
